/**
 * Admin → Blog — post list. Authoring happens in BlogPostEditor
 * (/dashboard/blog/new, /dashboard/blog/:id/edit).
 *
 * The marketing site is a static export, so publish/unpublish only change the
 * database until "Rebuild site" (or any marketing deploy) bakes the content
 * in — the banner under the header keeps that visible to editors.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, ExternalLink, Globe, Loader2, Plus, Rocket, Trash2, Undo2 } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { formatApiError } from '@/lib/format-api-error';
import {
  blogKeys,
  formatBlogDate,
  resolveBlogImageUrl,
  toggleBlogPostStatus,
  type AdminBlogPost,
  type BlogConfig,
  type BlogDeployResult,
  type BlogPostStatus,
} from '@/services/blog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type StatusFilter = 'all' | BlogPostStatus;

export function BlogPostsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionError, setActionError] = useState<string | null>(null);
  const [deployMessage, setDeployMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminBlogPost | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: config } = useQuery({
    queryKey: blogKeys.config,
    queryFn: async () => {
      const response = await apiClient.get<BlogConfig>('/api/v1/admin/blog/config');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const {
    data: posts,
    isLoading,
    error: listError,
  } = useQuery({
    queryKey: blogKeys.posts(statusFilter),
    queryFn: async () => {
      const params: Record<string, string> = { limit: '100' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await apiClient.get<AdminBlogPost[]>('/api/v1/admin/blog/posts', params);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const invalidatePosts = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'blog', 'posts'] });

  const handleToggleStatus = async (post: AdminBlogPost) => {
    setActionError(null);
    setBusyId(post.id);
    const response = await toggleBlogPostStatus(post);
    setBusyId(null);
    if (!response.success) {
      setActionError(formatApiError(response.error));
      return;
    }
    await invalidatePosts();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionError(null);
    setIsDeleting(true);
    const response = await apiClient.delete(`/api/v1/admin/blog/posts/${deleteTarget.id}`);
    setIsDeleting(false);
    setDeleteTarget(null);
    if (!response.success) {
      setActionError(formatApiError(response.error));
      return;
    }
    await invalidatePosts();
  };

  const handleDeploy = async () => {
    setActionError(null);
    setDeployMessage(null);
    setIsDeploying(true);
    const response = await apiClient.post<BlogDeployResult>('/api/v1/admin/blog/deploy');
    setIsDeploying(false);
    if (!response.success) {
      setActionError(formatApiError(response.error));
      return;
    }
    setDeployMessage(response.data?.message ?? null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Blog</h1>
          <p className="text-muted-foreground">
            Write, publish, and manage the posts on legacyorb.com/blog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDeploy} disabled={isDeploying}>
            {isDeploying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Rocket className="mr-2 h-4 w-4" />
            )}
            Rebuild site
          </Button>
          <Button asChild>
            <Link to="/dashboard/blog/new">
              <Plus className="mr-2 h-4 w-4" />
              New post
            </Link>
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        The marketing site is static — after publishing, unpublishing, or editing a live post, use{' '}
        <span className="font-medium text-foreground">Rebuild site</span> to push changes to
        legacyorb.com. Rebuilds take a few minutes.
      </p>

      {deployMessage && (
        <p className="rounded-md border border-orb-teal/40 bg-orb-teal-light px-4 py-3 text-sm text-orb-teal">
          {deployMessage}
        </p>
      )}
      {(actionError || listError) && (
        <p className="text-sm text-destructive">{actionError || (listError as Error).message}</p>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>All posts</CardTitle>
          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="published">Published</TabsTrigger>
              <TabsTrigger value="draft">Drafts</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Post</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(posts ?? []).map((post) => (
                  <TableRow key={post.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {post.featuredImage ? (
                          <img
                            src={resolveBlogImageUrl(
                              post.featuredImage,
                              config?.assetsPublicBaseUrl
                            )}
                            alt=""
                            className="h-10 w-16 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-16 rounded bg-gradient-to-br from-orb-gold/70 to-orb-teal/70" />
                        )}
                        <div>
                          <p className="font-medium">{post.title}</p>
                          <p className="text-xs text-muted-foreground">
                            /blog/{post.slug} · {post.readingTimeMinutes} min read
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                        {post.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {post.publishedAt ? formatBlogDate(post.publishedAt) : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatBlogDate(post.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      {post.status === 'published' && config?.assetsPublicBaseUrl && (
                        <Button variant="ghost" size="sm" asChild title="View on site">
                          <a
                            href={`${config.assetsPublicBaseUrl}/blog/${post.slug}/`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" asChild title="Edit">
                        <Link to={`/dashboard/blog/${post.id}/edit`}>
                          <Edit className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                        onClick={() => handleToggleStatus(post)}
                        disabled={busyId === post.id}
                      >
                        {busyId === post.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : post.status === 'published' ? (
                          <Undo2 className="h-4 w-4" />
                        ) : (
                          <Globe className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        onClick={() => setDeleteTarget(post)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(posts ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      {statusFilter === 'all' ? 'No posts yet.' : `No ${statusFilter} posts.`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the post from the CMS. If it is currently live, it stays on
              the site until the next rebuild. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
